<!-- 
Dialog for editing or creating an information session.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog sm">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Schedule an information session</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-setup" section="information-sessions"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12>
                <our-date-picker 
                  v-if="show"
                  :value="informationSession.expiryDate" 
                  :current-or-future="creating"
                  label="Session end date*" 
                  required autofocus 
                  @input="update('expiryDate', $event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-alert v-if="creating" :value="true" type="info">
          Set the end date to the last day you expect to send notices to providers about this session.
          You can change it later if needed, or if you decide to hold a new round of information sessions 
          you can schedule a new session after this session's end date is reached.
        </v-alert>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="busy" flat color="success" 
            type="submit" @click.prevent="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import * as mutation from "@/store/levy-collections/mutation-types";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      required: true
    },
    informationSessionId: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    }
  },
  data() {
    return {
      showValidationDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.editInformationSession;
    },
    informationSession() {
      return this.state.data;
    },
    busy() {
      return this.state.saving || this.state.loading;
    },
    creating() {
      return !this.informationSessionId;
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        if (this.creating) {
          this.$store.commit(mutation.EDIT_INFORMATION_SESSION_INITIALISE, this.levyCollectionId);
        } else {
          await this.$store.dispatch(action.EDIT_INFORMATION_SESSION_GET, {
            levyCollectionId: this.levyCollectionId,
            informationSessionId: this.informationSessionId
          });
        }
      }
    }
  },
  methods: {
    update(field, value) {
      this.$store.commit(mutation.EDIT_INFORMATION_SESSION_SET_DATA, { [field]: value });
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_INFORMATION_SESSION_SAVE))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
